// Remote Realtime Tools — admin page for registering / revoking sources, and
// flipping the per-org `remoteRealtimeTools` feature toggle on the MCP server.
//
// Registration mints a source token signed/persisted by the MCP server. The
// source uses it to open a WebSocket to `/sources/ws` and declares a tool
// manifest. MCP clients reach those tools through the fixed routing API:
//   `realtime_source_list` → `realtime_source_tools({ sourceId })` → `realtime_source_call({ sourceId, toolName, arguments })`
//
// When the toggle is OFF for the org, those three tools are not surfaced in
// `tools/list` to any MCP client.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState, type CSSProperties } from 'react';

import { useAuth } from '../../auth/AuthProvider';
import { EmptyState } from '../../components/common/EmptyState';
import { PageHeader } from '../../components/common/PageHeader';
import { SourceFilesPanel } from '../../components/realtime/SourceFilesPanel';
import {
  getFeatures,
  listSources,
  registerSource,
  revokeSource,
  setFeature,
  type IRegisteredSource,
  type IRegisteredSourceResponse,
} from '../../lib/mcp-server';

export const Route = createFileRoute('/internal/remote-realtime-tools')({
  component: RemoteRealtimeToolsPage,
});

const stack3: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' };
const stack4: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' };
const row: CSSProperties = { display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end' };
const mono: CSSProperties = {
  fontFamily: 'var(--ff-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-primary)',
  background: 'var(--bg-elevated, var(--bg-chrome))',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm, 6px)',
  padding: 'var(--sp-2) var(--sp-3)',
  wordBreak: 'break-all',
  whiteSpace: 'pre-wrap',
};

function RemoteRealtimeToolsPage() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const [sourceIdInput, setSourceIdInput] = useState('');
  const [issued, setIssued] = useState<IRegisteredSourceResponse | null>(null);

  const features = useQuery({
    enabled: Boolean(token),
    queryKey: ['mcp-features'],
    queryFn: () => getFeatures(token ?? ''),
  });

  const featureToggle = useMutation({
    mutationFn: (enabled: boolean) => setFeature('remoteRealtimeTools', enabled, token ?? ''),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['mcp-features'] });
    },
  });

  const list = useQuery({
    enabled: Boolean(token),
    queryKey: ['mcp-sources'],
    queryFn: () => listSources(token ?? ''),
    refetchInterval: 5_000,
  });

  const create = useMutation({
    mutationFn: () => registerSource(sourceIdInput.trim(), token ?? ''),
    onSuccess: async (res) => {
      setIssued(res);
      setSourceIdInput('');
      await qc.invalidateQueries({ queryKey: ['mcp-sources'] });
    },
  });

  const revoke = useMutation({
    mutationFn: (sourceId: string) => revokeSource(sourceId, token ?? ''),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['mcp-sources'] });
    },
  });

  const remoteEnabled = features.data?.remoteRealtimeTools ?? false;
  const featureSwitching = featureToggle.isPending || features.isLoading;

  return (
    <>
      <PageHeader />

      <div style={stack4}>
        {/* Feature toggle */}
        <div className="card">
          <div
            className="card-body"
            style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4)' }}
          >
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0 }}>Feature: remote realtime tools</h3>
              <div className="t-xs t-secondary" style={{ marginTop: 'var(--sp-1)' }}>
                When OFF, the <code>realtime_source_list</code> / <code>realtime_source_tools</code>{' '}
                / <code>realtime_source_call</code> tools are not exposed to any MCP client for your
                organization, even if sources are connected. Toggling rebuilds the per-org MCP
                server on the next request.
              </div>
              {featureToggle.error ? (
                <div
                  className="t-xs"
                  style={{ color: 'var(--text-danger, #c53030)', marginTop: 'var(--sp-1)' }}
                >
                  {featureToggle.error instanceof Error
                    ? featureToggle.error.message
                    : String(featureToggle.error)}
                </div>
              ) : null}
            </div>
            <FeatureSwitch
              checked={remoteEnabled}
              disabled={featureSwitching}
              onChange={(v) => featureToggle.mutate(v)}
            />
          </div>
        </div>

        {/* Register */}
        <div className="card">
          <div className="card-body" style={stack3}>
            <h3 style={{ margin: 0 }}>Register a new source</h3>
            <div className="field">
              <label className="field-label" htmlFor="source-id-input">
                Source id prefix
              </label>
              <div style={row}>
                <input
                  id="source-id-input"
                  className="input"
                  type="text"
                  placeholder="e.g. shop-floor-arm-7"
                  value={sourceIdInput}
                  onChange={(e) => setSourceIdInput(e.target.value)}
                  disabled={create.isPending}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  disabled={!token || !sourceIdInput.trim() || create.isPending}
                  onClick={() => create.mutate()}
                >
                  {create.isPending ? 'Registering…' : 'Register source'}
                </button>
              </div>
              <div className="field-help">
                The server appends a short random suffix so the final id is globally unique — e.g.{' '}
                <code>shop-floor-arm-7-k3p9zq</code>. The full id appears in public URLs and in{' '}
                <code>realtime_source_list</code>. Use a lowercase prefix of letters, digits, and
                hyphens; 1-57 chars; no leading/trailing hyphen.
              </div>
            </div>
            {create.error ? (
              <div className="t-xs" style={{ color: 'var(--text-danger, #c53030)' }}>
                {create.error instanceof Error ? create.error.message : String(create.error)}
              </div>
            ) : null}
          </div>
        </div>

        {/* Issued token (one-shot) */}
        {issued ? (
          <div
            className="card"
            style={{ borderColor: 'var(--border-strong, var(--border-subtle))' }}
          >
            <div className="card-body" style={stack3}>
              <h3 style={{ margin: 0 }}>
                Source registered{' '}
                <span className="badge badge--neutral">copy now — token is shown only once</span>
              </h3>
              <div>
                <div className="field-label">Source id</div>
                <div style={mono}>{issued.sourceId}</div>
              </div>
              <div>
                <div className="field-label">WebSocket URL</div>
                <div style={mono}>{issued.wsUrl}</div>
                <div className="t-xs t-secondary" style={{ marginTop: 'var(--sp-1)' }}>
                  The token is embedded in the <code>?token=</code> query string.
                </div>
              </div>
              <div>
                <div className="field-label">Example source (Node)</div>
                <CodeBlock code={buildExampleSource(issued)} />
                <div className="t-xs t-secondary" style={{ marginTop: 'var(--sp-2)' }}>
                  Save as <code>source.mjs</code>, run <code>npm i ws</code>, then{' '}
                  <code>node source.mjs</code>. The server dispatches <code>tools/call</code>{' '}
                  requests over the same socket and expects <code>{`{ jsonrpc, id, result }`}</code>{' '}
                  or <code>{`{ jsonrpc, id, error }`}</code> responses within 30s.
                </div>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => setIssued(null)}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Registered sources */}
        <div className="card">
          <div className="card-body" style={stack3}>
            <h3 style={{ margin: 0 }}>Registered sources</h3>
            <div className="t-xs t-secondary">
              All non-revoked registrations for your organization. Disconnected sources remain
              listed so you can revoke their tokens. Files uploaded by each source appear inline.
            </div>
            {list.isLoading ? (
              <div className="t-muted">Loading…</div>
            ) : list.error ? (
              <div className="t-xs" style={{ color: 'var(--text-danger, #c53030)' }}>
                {list.error instanceof Error ? list.error.message : String(list.error)}
              </div>
            ) : !list.data || list.data.length === 0 ? (
              <EmptyState
                title="No sources registered"
                description="Register a source above to get a token, then have it open the WebSocket to expose its tools."
              />
            ) : (
              <div style={stack3}>
                {list.data.map((d) => (
                  <SourceCard
                    key={d.sourceId}
                    source={d}
                    token={token ?? ''}
                    onRevoke={() => {
                      if (
                        window.confirm(
                          `Revoke '${d.sourceId}'? Its token will stop working and all uploaded files will be deleted.`,
                        )
                      ) {
                        revoke.mutate(d.sourceId);
                      }
                    }}
                    revoking={revoke.isPending && revoke.variables === d.sourceId}
                  />
                ))}
              </div>
            )}
            {revoke.error ? (
              <div className="t-xs" style={{ color: 'var(--text-danger, #c53030)' }}>
                {revoke.error instanceof Error ? revoke.error.message : String(revoke.error)}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Build a self-contained Node example that registers two demo tools (`echo`
 * and `reboot`) with the MCP server, then handles `tools/call` over the same
 * WebSocket. The user can copy-paste this into a file and run it, or hand it
 * to an LLM as a starting template.
 */
function buildExampleSource(issued: IRegisteredSourceResponse): string {
  return `// ${issued.sourceId} — Realtime Source for the cortex MCP server.
// Requires: npm i @formant/realtime-tools
//
// Usage:
//   node source.mjs                              # uses the constants below
//   node source.mjs <wsUrl> <sourceId>           # override at runtime
//   SOURCE_WS_URL=... SOURCE_ID=... node source.mjs

import { RealtimeToolSourceClient } from '@formant/realtime-tools';

// ──────────────────────────────────────────────────────────────────────────
// Defaults — replace these after re-registering the source.
// ──────────────────────────────────────────────────────────────────────────

const DEFAULT_WS_URL = ${JSON.stringify(issued.wsUrl)};
const DEFAULT_SOURCE_ID = ${JSON.stringify(issued.sourceId)};

const wsUrl = process.argv[2] ?? process.env.SOURCE_WS_URL ?? DEFAULT_WS_URL;
const sourceId = process.argv[3] ?? process.env.SOURCE_ID ?? DEFAULT_SOURCE_ID;

// ──────────────────────────────────────────────────────────────────────────
// Client + tools
// ──────────────────────────────────────────────────────────────────────────

const client = new RealtimeToolSourceClient({ wsUrl, sourceId });

client.tool({
  name: 'echo',
  description: 'Echo back the provided message.',
  inputSchema: {
    type: 'object',
    properties: { message: { type: 'string' } },
    required: ['message'],
    additionalProperties: false,
  },
  handler: async ({ message }) => ({ echoed: String(message ?? '') }),
});

client.tool({
  name: 'snapshot',
  description:
    'Upload a 1x1 PNG snapshot from this source and return its public URL.',
  inputSchema: { type: 'object', properties: {}, additionalProperties: false },
  handler: async (_args, ctx) => {
    // Minimal valid 1x1 transparent PNG.
    const png = Buffer.from(
      'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
      'base64',
    );
    const { url, mimeType, sizeBytes } = await ctx.uploadFile(png, 'image/png');
    return { url, mimeType, sizeBytes };
  },
});

client.start();
`;
}

/**
 * Read-only code block with a Copy button pinned to the top-right of the
 * outer card. The inner <pre> is the scroll surface; the button lives in
 * the (non-scrolling) outer container so it stays visible no matter where
 * the user is in the code.
 */
function CodeBlock({ code }: { code: string }) {
  const [label, setLabel] = useState('Copy');
  const outer: CSSProperties = {
    position: 'relative',
    background: 'var(--bg-elevated, var(--bg-chrome))',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm, 6px)',
    overflow: 'hidden',
  };
  const scroll: CSSProperties = {
    fontFamily: 'var(--ff-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
    fontSize: 'var(--fs-xs)',
    color: 'var(--text-primary)',
    padding: 'var(--sp-3)',
    paddingRight: 'calc(var(--sp-3) + 80px)',
    maxHeight: 360,
    overflow: 'auto',
    whiteSpace: 'pre',
    margin: 0,
  };
  const btn: CSSProperties = {
    position: 'absolute',
    top: 'var(--sp-2)',
    right: 'var(--sp-2)',
    zIndex: 1,
  };
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setLabel('Copied');
      setTimeout(() => setLabel('Copy'), 1500);
    } catch {
      setLabel('Copy failed');
      setTimeout(() => setLabel('Copy'), 1500);
    }
  }
  return (
    <div style={outer}>
      <button type="button" className="btn btn--secondary btn--xs" onClick={copy} style={btn}>
        {label}
      </button>
      <pre style={scroll}>{code}</pre>
    </div>
  );
}

/**
 * Visual toggle switch. The design system doesn't ship one yet so this is an
 * inline implementation: a 36×20 rounded pill with a thumb that slides on
 * `checked`. Colors come from the global CSS variables.
 */
function FeatureSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  const trackOn = 'var(--interface-iris, #4f46e5)';
  const trackOff = 'var(--border-strong, var(--border-subtle))';
  const wrap: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--sp-2)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    userSelect: 'none',
  };
  const track: CSSProperties = {
    position: 'relative',
    width: 36,
    height: 20,
    borderRadius: 999,
    background: checked ? trackOn : trackOff,
    transition: 'background 120ms ease',
    flexShrink: 0,
  };
  const thumb: CSSProperties = {
    position: 'absolute',
    top: 2,
    left: checked ? 18 : 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: 'var(--bg-surface, #fff)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
    transition: 'left 120ms ease',
  };
  return (
    <label style={wrap}>
      <input
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{
          position: 'absolute',
          opacity: 0,
          width: 1,
          height: 1,
          pointerEvents: 'none',
        }}
      />
      <span style={track}>
        <span style={thumb} />
      </span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 'var(--fw-med, 500)' }}>
        {checked ? 'Enabled' : 'Disabled'}
      </span>
    </label>
  );
}

/**
 * A single registered source rendered as a card with header (name, status,
 * id, tools, revoke action) and an inline files panel underneath.
 */
function SourceCard({
  source,
  token,
  onRevoke,
  revoking,
}: {
  source: IRegisteredSource;
  token: string;
  onRevoke: () => void;
  revoking: boolean;
}) {
  const header: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--sp-3)',
    flexWrap: 'wrap',
  };
  return (
    <div
      className="card"
      style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)' }}
    >
      <div
        className="card-body"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}
      >
        <div style={header}>
          <span
            style={{
              color: 'var(--text-primary)',
              fontWeight: 'var(--fw-med, 500)',
              fontFamily: 'var(--ff-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
            }}
          >
            {source.sourceId}
          </span>
          {source.connected ? (
            <span className="badge badge--success">connected</span>
          ) : (
            <span className="badge badge--neutral">offline</span>
          )}
          <span className="t-xs t-muted" style={{ marginLeft: 'auto' }}>
            registered {new Date(source.createdAt).toLocaleString()}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            disabled={revoking}
            onClick={onRevoke}
            style={{ color: 'var(--text-danger, #c53030)' }}
          >
            {revoking ? 'Revoking…' : 'Revoke'}
          </button>
        </div>
        <div className="t-xs t-muted">
          Tools:{' '}
          {!source.connected
            ? '— (source is offline)'
            : source.tools.length === 0
              ? '— (awaiting register)'
              : source.tools.map((t) => t.name).join(', ')}
        </div>
        <div>
          <div className="field-label" style={{ marginBottom: 'var(--sp-1)' }}>
            Files
          </div>
          <SourceFilesPanel sourceId={source.sourceId} token={token} />
        </div>
      </div>
    </div>
  );
}
